import React, { useState, useRef, useEffect } from 'react';

/**
 * Drop-in replacement for a plain <select> when the option list is long (products, customers,
 * vendors, batches...) and scrolling a raw OS dropdown to find one entry is painful — item #13
 * on the known-issues list, added 2026-08-19.
 *
 * Keeps the same mental model as a native select: pass `options` as [{ value, label }], a
 * `value`, and `onChange(value)`. Typing filters the list (case-insensitive substring match on
 * label); arrow keys move the highlight, Enter selects, Escape closes. Not a full combobox
 * library — just enough to make a 50+ row list searchable without pulling in a dependency.
 */
const SearchableSelect = ({ label, options, value, onChange, placeholder, disabled, required, style }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => String(o.value) === String(value));

  useEffect(() => {
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = query.trim() === ''
    ? options
    : options.filter(o => o.label.toLowerCase().includes(query.trim().toLowerCase()));

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setHighlight(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const pick = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlight]) pick(filtered[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  const control = (
    <div ref={rootRef} style={{ position: 'relative', ...(label ? {} : style) }}>
      {!open ? (
        <button type="button" onClick={openDropdown} disabled={disabled}
          style={{
            width: '100%', textAlign: 'left', padding: '7px 10px', border: '1px solid var(--color-border-strong)',
            borderRadius: 6, fontSize: 13, background: disabled ? 'var(--color-bg-tertiary)' : '#fff',
            color: selected ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)', cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selected ? selected.label : (placeholder || '— Select —')}
          </span>
          <span style={{ color: 'var(--color-text-tertiary)', marginLeft: 6 }}>▾</span>
        </button>
      ) : (
        <input
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setHighlight(0); }}
          onKeyDown={onKeyDown}
          placeholder={`Search... (${options.length} options)`}
          required={required}
          style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--color-primary)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
        />
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 2,
          background: '#fff', border: '1px solid var(--color-border-strong)', borderRadius: 6, maxHeight: 240,
          overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', color: 'var(--color-text-tertiary)', fontSize: 13 }}>No matches</div>
          ) : filtered.map((opt, i) => (
            <div key={opt.value} onMouseDown={() => pick(opt)} onMouseEnter={() => setHighlight(i)}
              style={{
                padding: '7px 12px', fontSize: 13, cursor: 'pointer',
                background: i === highlight ? 'var(--color-info-light)' : '#fff',
                color: String(opt.value) === String(value) ? 'var(--color-primary)' : 'var(--color-text-primary)',
                fontWeight: String(opt.value) === String(value) ? 600 : 400,
              }}>
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (!label) return control;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...style }}>
      <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{label}</label>
      {control}
    </div>
  );
};

export default SearchableSelect;
